/**
 * Friendly error formatter.
 *
 * `String(err)` produces `[object Object]` for Supabase / PostgREST errors,
 * which are plain objects with `{ code, message, details, hint }`. This
 * helper handles every shape we've encountered:
 *
 *   - Error instances        -> err.message
 *   - PostgrestError objects -> "<message> (<hint>)"
 *   - Strings                -> as-is
 *   - Anything else          -> JSON.stringify, falling back to toString
 *
 * Special-cased: errors raised by `shop.fn_assert_vendor_not_suspended`
 * (`hint = 'vendor_suspended'`, or message containing the sentinel) are
 * rewritten to the friendly toast copy demanded by the vendor-suspension
 * lockdown spec so every existing `toast.push(describeError(e), 'warn')`
 * call site inherits the message without a per-mutation edit.
 */
export function describeError(err: unknown): string {
  if (isVendorSuspendedError(err)) {
    return 'Your account is suspended — please contact admins via Help.';
  }
  if (err == null) return 'Unknown error';
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (typeof err === 'object') {
    const obj = err as Record<string, unknown>;
    if (typeof obj.message === 'string' && obj.message.length > 0) {
      const hint = typeof obj.hint === 'string' && obj.hint.length > 0 ? ` (${obj.hint})` : '';
      return obj.message + hint;
    }
    try {
      return JSON.stringify(err);
    } catch {
      return Object.prototype.toString.call(err);
    }
  }
  return String(err);
}

/**
 * Returns true if the error is the `vendor_suspended` SQLSTATE P0001
 * raised by `shop.fn_assert_vendor_not_suspended`. Detection covers
 * both the structured PostgREST shape (`{ hint: 'vendor_suspended' }`)
 * AND a free-form Error whose message starts with `vendor_suspended:`
 * so a chain that loses the structured shape still resolves.
 */
export function isVendorSuspendedError(err: unknown): boolean {
  if (!err) return false;
  if (err instanceof Error) {
    return err.message.startsWith('vendor_suspended:') || err.message.includes('vendor_suspended');
  }
  if (typeof err === 'string') return err.includes('vendor_suspended');
  if (typeof err === 'object') {
    const obj = err as Record<string, unknown>;
    if (typeof obj.hint === 'string' && obj.hint === 'vendor_suspended') return true;
    if (typeof obj.message === 'string' && obj.message.includes('vendor_suspended')) return true;
  }
  return false;
}
